// src/views/AccommodationProvider/ProviderAddPropertyCampuses.js
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col, Alert, Button, Input, FormGroup, Label } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderAddPropertyCampuses = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // State
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [availableCampuses, setAvailableCampuses] = useState([]);
  const [selectedCampusId, setSelectedCampusId] = useState('');
  const [addedCampuses, setAddedCampuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Load institutions on mount
  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const response = await axiosInstance.get('/api/institution_view/');
        console.log('Institution API response:', response.data);
        console.log('Is array?', Array.isArray(response.data));
        
        if (response.data && Array.isArray(response.data)) {
          console.log('First institution:', response.data[0]);
          setInstitutions(response.data);
        }
      } catch (error) {
        console.error('Error loading institutions:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load institutions. Please try again.',
          visible: true
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadInstitutions();
  }, []);

  // Update available campuses when institution is selected
  useEffect(() => {
    console.log('Institution selection changed:', selectedInstitution);
    console.log('Available institutions:', institutions);
    
    if (selectedInstitution) {
      const institution = institutions.find(inst => inst.id === parseInt(selectedInstitution));
      console.log('Found institution:', institution);
      
      if (institution && institution.campuses) {
        console.log('Setting available campuses:', institution.campuses);
        setAvailableCampuses(institution.campuses);
        setSelectedCampusId(''); // Reset campus selection
      } else {
        console.log('No campuses found for institution');
        setAvailableCampuses([]);
      }
    } else {
      setAvailableCampuses([]);
      setSelectedCampusId('');
    }
  }, [selectedInstitution, institutions]);

  // Handle institution selection
  const handleInstitutionChange = (e) => {
    setSelectedInstitution(e.target.value);
  };

  // Handle adding a campus to the list
  const handleAddCampus = () => {
    if (!selectedCampusId) {
      setAlert({
        type: 'warning',
        message: 'Please select a campus to add.',
        visible: true
      });
      return;
    }

    // Check if campus is already added
    if (addedCampuses.some(c => c.id === parseInt(selectedCampusId))) {
      setAlert({
        type: 'warning',
        message: 'This campus has already been added.',
        visible: true
      });
      return;
    }

    // Find the campus details
    const campus = availableCampuses.find(c => c.id === parseInt(selectedCampusId));
    const institution = institutions.find(inst => inst.id === parseInt(selectedInstitution));
    
    if (campus && institution) {
      setAddedCampuses([...addedCampuses, {
        id: campus.id,
        name: campus.name,
        institution_name: institution.name,
        institution_abbrev: institution.abbrev
      }]);
      setSelectedCampusId(''); // Reset campus selection
      setAlert({
        type: 'success',
        message: `Added ${campus.name} to your service list.`,
        visible: true
      });
    }
  };

  // Handle removing a campus from the list
  const handleRemoveCampus = (campusId) => {
    setAddedCampuses(addedCampuses.filter(c => c.id !== campusId));
  };

  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Campus Selection - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Campus Selection - ${providerName || 'Your Organization'}`;
    } else {
      return 'Campus Selection';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const handleBackToContact = () => {
    navigate('/provider-add-property-contact');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert({ type: '', message: '', visible: false });

    if (addedCampuses.length === 0) {
      setAlert({
        type: 'warning',
        message: 'Please add at least one campus to serve.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Store selected campuses in session
      const campusIds = addedCampuses.map(campus => campus.id);
      
      await axiosInstance.post('/api/property/campuses/store/', {
        campus_ids: campusIds
      });

      setAlert({
        type: 'success',
        message: 'Campus selection saved. Proceed to bed allocation.',
        visible: true
      });

      // Navigate to bed allocation step
      setTimeout(() => {
        navigate('/provider-add-property-bed-allocation');
      }, 1000);
    } catch (error) {
      console.error('Error saving campus selection:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to save campus selection. Please try again.',
        visible: true
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary" className="me-2">
                Step 4 of 5
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToContact}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Contact
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col md="12">
            <Alert color={alert.type} toggle={() => setAlert({...alert, visible: false})}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Campus Selection Form */}
      <Row className="mb-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-building me-2 text-primary"></i>
                Select Campuses to Serve
              </CardTitle>

              <Alert color="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Select the institutions and campuses you want to serve. You can add multiple campuses from different institutions.
              </Alert>

              {/* Institution Dropdown */}
              <Row className="mb-3">
                <Col md="6">
                  <FormGroup>
                    <Label for="institutionSelect">
                      <i className="bi bi-bank me-2"></i>
                      Select Institution <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      id="institutionSelect"
                      value={selectedInstitution}
                      onChange={handleInstitutionChange}
                      disabled={isLoading}
                    >
                      <option value="">-- Select Institution --</option>
                      {[...institutions].sort((a, b) => a.name.localeCompare(b.name)).map(institution => (
                        <option key={institution.id} value={institution.id}>
                          {institution.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label for="campusSelect">
                      <i className="bi bi-building me-2"></i>
                      Select Campus <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      id="campusSelect"
                      value={selectedCampusId}
                      onChange={(e) => setSelectedCampusId(e.target.value)}
                      disabled={!selectedInstitution || availableCampuses.length === 0}
                    >
                      <option value="">-- Select Campus --</option>
                      {availableCampuses.map(campus => (
                        <option key={campus.id} value={campus.id}>
                          {campus.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {/* Add Campus Button */}
              <Row className="mb-4">
                <Col md="12">
                  <Button 
                    color="primary" 
                    onClick={handleAddCampus}
                    disabled={!selectedCampusId}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Campus to List
                  </Button>
                </Col>
              </Row>

              {/* Added Campuses List */}
              {addedCampuses.length > 0 && (
                <>
                  <h5 className="mb-3">
                    <i className="bi bi-list-check me-2 text-success"></i>
                    Selected Campuses ({addedCampuses.length})
                  </h5>
                  <div className="list-group mb-4">
                    {addedCampuses.map(campus => (
                      <div key={campus.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{campus.name}</strong>
                          <br />
                          <small className="text-muted">
                            {campus.institution_name} ({campus.institution_abbrev})
                          </small>
                        </div>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleRemoveCampus(campus.id)}
                        >
                          <i className="bi bi-trash"></i> Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading institutions...</p>
                </div>
              )}

              {/* No Campuses Added Warning */}
              {!isLoading && addedCampuses.length === 0 && (
                <Alert color="info">
                  <i className="bi bi-info-circle me-2"></i>
                  No campuses added yet. Please select an institution and campus above, then click "Add Campus to List".
                </Alert>
              )}

              {/* Submit Button */}
              <Row className="mt-4">
                <Col md="12" className="text-center">
                  <Button 
                    color="primary" 
                    size="lg"
                    onClick={handleSubmit}
                    disabled={regStatus !== '2' || isSubmitting || addedCampuses.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        Next
                        <i className="bi bi-arrow-right ms-2"></i>
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                About Campus Selection
              </CardTitle>
              <p className="small text-muted mb-0">
                Select all campuses where students will have access to your property. 
                Choose an institution from the dropdown, then select one of its campuses to add to your list. 
                You can add multiple campuses from different institutions.
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderAddPropertyCampuses;
